import Mode from '../const/Mode';
import VcomProvider from '../../../../VcomProvider';
import AlarmType from '../../../../portlet/viiu/alarms/const/AlarmType';
import translate from '~/zf/frontend/common/localization/translate';
import ComponentType from '../../../../portlet/viiu/alarms/const/ComponentType';
function getSystemIdFromVcomProvider() {
    return VcomProvider.adminSystemId;
}
function mapToCreateConfig(config) {
    const systemId = getSystemIdFromVcomProvider() ?? config.systemId;
    /* eslint-disable-next-line fp/no-throw */
    if (!systemId)
        throw new Error('system ID not provided');
    const { alarmInformation: { alarmType, alarmTypeId, alarmId } } = config;
    const allowedAlarmTypeIds = [
        AlarmType.COMMUNICATION_LOSS,
        AlarmType.TOTAL_OUTAGE,
        AlarmType.MISPRODUCTION,
    ];
    return {
        systemId,
        start: config.start.toUTCString(),
        end: config.end.toUTCString(),
        cascade: allowedAlarmTypeIds.includes(alarmTypeId),
        alarmTypeId,
        reason: config.reason.length > 0 ? config.reason : `${alarmType}, ID ${alarmId}`,
        mutedComponent: config.mutedComponent,
    };
}
function mapToScheduleConfigConfig(config) {
    const systemId = getSystemIdFromVcomProvider() ?? config.systemId;
    /* eslint-disable-next-line fp/no-throw */
    if (!systemId)
        throw new Error('system ID not provided');
    return {
        systemId,
        start: config.start.toUTCString(),
        end: config.end.toUTCString(),
        cascade: config.shouldCascade,
        alarmTypeId: null,
        reason: config.reason,
        mutedComponent: config.mutedComponent,
    };
}
function mapToEditConfig(config) {
    /* eslint-disable-next-line fp/no-throw */
    if (!config.id)
        throw new Error('config ID not provided');
    return {
        id: config.id,
        systemId: getSystemIdFromVcomProvider(),
        start: config.start.toUTCString(),
        end: config.end.toUTCString(),
        reason: config.reason,
    };
}
function mapToReactivateConfig(config) {
    /* eslint-disable-next-line fp/no-throw */
    if (!config.id)
        throw new Error('config ID not provided');
    return {
        id: config.id,
        systemId: getSystemIdFromVcomProvider(),
    };
}
function mapConfiguration(config, mode) {
    const mapper = {
        [Mode.CREATE]: (createConfig) => mapToCreateConfig(createConfig),
        [Mode.SCHEDULE]: (scheduledConfigConfig) => mapToScheduleConfigConfig(scheduledConfigConfig),
        [Mode.EDIT]: (updateConfig) => mapToEditConfig(updateConfig),
        [Mode.REACTIVATE]: (reactivateConfig) => mapToReactivateConfig(reactivateConfig),
    };
    return mapper[mode](config);
}
function getAffectedDevicesAsString(mutedComponent) {
    if (mutedComponent.childComponentsSummary === null
        && mutedComponent.componentType !== ComponentType.SYSTEM) {
        return [];
    }
    const { inverterCount, scbCount, otherCount, } = mutedComponent.childComponentsSummary ?? { inverterCount: 0, scbCount: 0, otherCount: 0 };
    const invertersTranslation = translate('viiu.componentMuting.INVERTERS');
    const stringCombinerBoxesTranslation = translate('viiu.componentMuting.STRING_COMBINER_BOXES');
    const otherDevicesTranslation = translate('viiu.componentMuting.OTHER_DEVICES');
    switch (mutedComponent.componentType) {
        case ComponentType.DATALOGGER:
            return [`${inverterCount} ${invertersTranslation}, ${scbCount} ${stringCombinerBoxesTranslation}, ${otherCount} ${otherDevicesTranslation}`];
        case ComponentType.INVERTER:
            return [`${scbCount} ${stringCombinerBoxesTranslation}`];
        case ComponentType.SYSTEM:
            return mutedComponent.childComponentsSummary ? [translate('viiu.componentMuting.ALL_COMPONENTS')] : [];
        case ComponentType.STRINGBOX:
            return mutedComponent.childComponentsSummary ? [translate('viiu.componentMuting.ALL_INPUTS')] : [];
        default: return [];
    }
}
function buildAffectedDevicesTree(affectedDevices) {
    const [title] = affectedDevices;
    const rest = affectedDevices.slice(1);
    return {
        title,
        children: rest.length > 0 ? [buildAffectedDevicesTree(rest)] : [],
    };
}
function mapMutedComponentToComponentTree(config) {
    const affectedDevices = getAffectedDevicesAsString(config);
    const children = affectedDevices.length !== 0
        ? buildAffectedDevicesTree(affectedDevices)
        : null;
    return {
        title: config.componentName,
        children: children ? [
            children,
        ] : [],
    };
}
const MappingService = {
    mapConfiguration,
    mapMutedComponentToComponentTree,
    getAffectedDevicesAsString,
};
export default MappingService;
